import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Box, Typography } from '@mui/material';

ChartJS.register(...registerables);

const YearlyEngagementChart = ({ data = [] }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const isHoveringRef = useRef(false);

    // Default data if none provided
    const defaultData = [
        { year: '1st Year', total_visits: 1800, active_users: 500 },
        { year: '2nd Year', total_visits: 3000, active_users: 650 },
        { year: '3rd Year', total_visits: 1000, active_users: 300 },
        { year: '4th Year', total_visits: 2200, active_users: 800 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.year || item.label);
    const totalVisits = chartData.map(item => item.total_visits || item.totalVisits || 0);
    const activeUsers = chartData.map(item => item.active_users || item.activeUsers || 0);
    const maxValue = Math.max(...totalVisits, ...activeUsers, 100);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');

            // Calculate appropriate step size based on max value
            const stepSize = Math.ceil(maxValue / 4 / 500) * 500 || 500;
            const yMax = Math.ceil(maxValue * 1.2 / stepSize) * stepSize;

            // Custom plugin to show labels only on hover
            const hoverLabelsPlugin = {
                id: 'hoverLabelsPlugin',
                afterDatasetsDraw(chart) {
                    // Only draw labels if we're hovering
                    if (!isHoveringRef.current) return;

                    const { ctx } = chart;

                    // Draw labels for both datasets
                    chart.data.datasets.forEach((dataset, datasetIndex) => {
                        const meta = chart.getDatasetMeta(datasetIndex);
                        const color = datasetIndex === 0 ? '#d0d0d0' : '#7ed57e'; // Gray for Total, Green for Active

                        ctx.save();
                        meta.data.forEach((bar, index) => {
                            const value = dataset.data[index];
                            const x = bar.x;
                            const y = bar.y;

                            // Draw the label above the bar
                            ctx.fillStyle = color;
                            ctx.font = '600 12px "Inter", sans-serif';
                            ctx.textAlign = 'center';
                            ctx.textBaseline = 'bottom';
                            ctx.fillText(value, x, y - 5);
                        });
                        ctx.restore();
                    });
                }
            };

            chartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Total Visits',
                            data: totalVisits,
                            backgroundColor: '#e8e8e8',
                            borderColor: '#d0d0d0',
                            borderWidth: 1,
                            borderRadius: 4,
                            barThickness: 40,
                        },
                        {
                            label: 'Active Users',
                            data: activeUsers,
                            backgroundColor: '#c8f4c8',
                            borderColor: '#7ed57e',
                            borderWidth: 1,
                            borderRadius: 4,
                            barThickness: 40,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    onHover: (event, activeElements, chart) => {
                        isHoveringRef.current = true;
                        chart.update('none');
                    },
                    plugins: {
                        legend: {
                            display: true,
                            position: 'top',
                            align: 'end',
                            labels: {
                                boxWidth: 12,
                                boxHeight: 12,
                                padding: 20,
                                color: '#666666',
                                font: { size: 12, family: '"Inter", sans-serif' },
                            },
                        },
                        datalabels: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            titleColor: '#333',
                            bodyColor: '#333',
                            borderColor: '#e0e0e0',
                            borderWidth: 1,
                            padding: 10,
                            displayColors: true,
                        }
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            border: {
                                display: true,
                                color: '#e0e0e0',
                            },
                            ticks: {
                                color: '#666666',
                                font: { size: 12, family: '"Inter", sans-serif' },
                            },
                        },
                        y: {
                            min: 0,
                            max: yMax,
                            ticks: {
                                stepSize: stepSize,
                                color: '#666666',
                                font: { size: 12, family: '"Inter", sans-serif' },
                            },
                            grid: {
                                color: '#f0f0f0',
                                drawBorder: false,
                            },
                            border: {
                                display: false,
                            },
                            title: {
                                display: true,
                                text: 'No. of Users',
                                color: '#999999',
                                font: { size: 12, family: '"Inter", sans-serif' },
                            }
                        },
                    },
                },
                plugins: [hoverLabelsPlugin]
            });

            // Add mouse leave listener to hide labels when mouse leaves
            const canvas = chartRef.current;
            const handleMouseLeave = () => {
                isHoveringRef.current = false;
                if (chartInstance.current) {
                    chartInstance.current.update('none');
                }
            };
            canvas.addEventListener('mouseleave', handleMouseLeave);

            return () => {
                canvas.removeEventListener('mouseleave', handleMouseLeave);
                if (chartInstance.current) chartInstance.current.destroy();
            };
        }
    }, [data, labels, totalVisits, activeUsers, maxValue]);

    // If no data, show a message
    if (data.length === 0 && chartData === defaultData) {
        // Use default data
    }

    return <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />;
};

export default YearlyEngagementChart;
